import { ReactElement, useMemo } from 'react';
import {
  ForecastKpiViewOption,
  ForecastResultsData
} from '@blisspointmedia/bpm-types/dist/Forecasting';
import { BPMTable, SuperTopItemType } from '../../../Components/StickyTable';
import { dateToMddyy } from '../../../utils/date-utils';
import {
  ForecastChartViewLabels,
  formatCpm,
  formatSpend,
  FullForecastSelection
} from '../forecasting-constants';
import { formatForecastViewLabel, getKpiFormatter } from '../forecasting-utils';

interface ForecastResultsTableProps {
  data: ForecastResultsData[];
  forecast: FullForecastSelection;
}

export const ForecastResultsTable = ({
  data,
  forecast
}: ForecastResultsTableProps): ReactElement => {
  const [tableHeaders, tableSuperHeaders] = useMemo(() => {
    const { outcomeVar, kpiName, primaryBudget } = forecast;
    const totalLabel = formatForecastViewLabel(outcomeVar, kpiName, ForecastKpiViewOption.TOTAL);
    const efficiencyLabel = ForecastChartViewLabels[outcomeVar][ForecastKpiViewOption.EFFICIENCY];
    const formatTotalKpi = getKpiFormatter(outcomeVar, ForecastKpiViewOption.TOTAL);
    const formatEfficiencyKpi = getKpiFormatter(outcomeVar, ForecastKpiViewOption.EFFICIENCY);

    const baseHeaderProps = {
      flex: true
    };

    const headers = [
      {
        ...baseHeaderProps,
        name: 'date',
        label: 'Date',
        renderer: (row: ForecastResultsData): string => dateToMddyy(row.date)
      },
      {
        ...baseHeaderProps,
        name: 'spend',
        label: 'Spend',
        renderer: (row: ForecastResultsData): string => formatSpend(row[`${primaryBudget}-spend`])
      },
      {
        ...baseHeaderProps,
        name: 'cpm',
        label: 'CPM',
        renderer: (row: ForecastResultsData): string => formatCpm(row[`${primaryBudget}-cpm`])
      },
      {
        ...baseHeaderProps,
        name: 'forecast',
        label: `${kpiName} Estimate`,
        renderer: (row: ForecastResultsData): string => formatTotalKpi(row.predicted_kpi)
      },
      {
        ...baseHeaderProps,
        name: 'forecastCILower',
        label: `${kpiName} Lower CI`,
        renderer: (row: ForecastResultsData): string => {
          const ci = row.predicted_kpi_ci;
          return ci ? formatTotalKpi(ci[0]) : '';
        }
      },
      {
        ...baseHeaderProps,
        name: 'forecastCIUpper',
        label: `${kpiName} Upper CI`,
        renderer: (row: ForecastResultsData): string => {
          const ci = row.predicted_kpi_ci;
          return ci ? formatTotalKpi(ci[1]) : '';
        }
      },
      {
        ...baseHeaderProps,
        name: 'efficiency',
        label: `${efficiencyLabel} Estimate`,
        renderer: (row: ForecastResultsData): string =>
          formatEfficiencyKpi(row.predicted_efficiency)
      },
      {
        ...baseHeaderProps,
        name: 'efficiencyCILower',
        label: `${efficiencyLabel} Lower CI`,
        renderer: (row: ForecastResultsData): string => {
          const ci = row.predicted_efficiency_ci;
          return ci ? formatEfficiencyKpi(ci[0]) : '';
        }
      },
      {
        ...baseHeaderProps,
        name: 'efficiencyCIUpper',
        label: `${efficiencyLabel} Upper CI`,
        renderer: (row: ForecastResultsData): string => {
          const ci = row.predicted_efficiency_ci;
          return ci ? formatEfficiencyKpi(ci[1]) : '';
        }
      }
    ];

    const superHeaders: SuperTopItemType<string>[] = [
      { span: 3, data: '' },
      { span: 3, data: totalLabel },
      { span: 3, data: efficiencyLabel }
    ];

    return [headers, superHeaders];
  }, [forecast]);

  return (
    <div className='forecast-results-table-container'>
      <BPMTable
        data={data}
        headers={tableHeaders}
        superHeaders={tableSuperHeaders}
        filterBar={false}
      />
    </div>
  );
};
